import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, Plus, ArrowDownRight, ArrowUpRight,
  Loader2, ShieldCheck, Receipt, TrendingUp, Zap,
} from "lucide-react";
import CouponApplyField from "@/components/checkout/CouponApplyField";

/* Wallet page — /vendor/wallet

   - Big balance card at the top (paise → rupees, tabular-nums, huge type).
   - Top-up quick-picks (₹500 / ₹1,000 / ₹5,000) + custom amount input.
     Custom minimum is ₹100 (backend enforces).
   - GSTIN field so the vendor can attach it to the invoice generated
     on this specific top-up. If the vendor already has one on file we
     pre-fill and mark it read-only.
   - Recent transactions list with type badges (top-up / debit) and the
     `balance_after_paise` shown for a mini running-total feel.
*/

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(
    Number(paise || 0) / 100
  );
const rupeesR = (r) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(Number(r || 0));

const QUICK_PICKS = [500, 1000, 5000];

const REASON_LABEL = {
  wallet_topup: "Wallet top-up",
  topup: "Wallet top-up",
  lead_overage: "Lead overage",
  email_retarget: "Email retarget",
  whatsapp_retarget: "WhatsApp retarget",
  sms_retarget: "SMS retarget",
  rcs_retarget: "RCS retarget",
  admin_adjustment: "Admin adjustment",
  promo_grant: "Promotional credit",
  coupon_bonus: "Coupon bonus credit",
  coupon_redemption: "Coupon redemption",
  campaign_fee: "Campaign activation",
  listing_duration: "Listing duration",
  featured_slot: "Featured slot",
};

const ensureRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(!!window.Razorpay);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function WalletPage() {
  const [state, setState] = useState({ loading: true, data: null });
  const [amount, setAmount] = useState(1000);
  const [gstNumber, setGstNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [vendorGst, setVendorGst] = useState("");
  // ── Coupon state ─────────────────────────────────────────────────
  // `couponApplied` holds the applied coupon snapshot returned by the
  // most recent successful validate call. Kept out of `state` to keep
  // wallet-summary reloads from clobbering it. Cleared on Remove or
  // when the vendor changes the amount.
  const [couponApplied, setCouponApplied] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, me] = await Promise.all([api.walletSummary(), api.get("/auth/vendor/me")]);
      setState({ loading: false, data: w.data });
      const g = (me.data?.gst_number || "").trim();
      setVendorGst(g);
      if (g) setGstNumber(g);
    } catch (e) {
      setState({ loading: false, data: null });
      toast.error(e?.response?.data?.detail || "Couldn't load wallet");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const topup = async () => {
    if (busy) return;
    const rup = Number(amount);
    if (!Number.isFinite(rup) || rup < 100) {
      toast.error("Minimum top-up is ₹100.");
      return;
    }
    setBusy(true);
    try {
      const orderRes = await api.walletTopupCreate({
        amount_rupees: rup,
        gst_number: (gstNumber || "").trim() || null,
        coupon_code: couponApplied?.code || null,
      });

      // ── Fully-covering coupon redemption — no Razorpay path ─────
      // Server signals this with `redeemed: true` and no razorpay
      // handshake. Wallet is already credited via the ledger; we
      // just refresh the UI + show a DISTINCT success toast so the
      // vendor never mistakes it for a paid top-up.
      if (orderRes.data?.redeemed) {
        const cr = orderRes.data.coupon_redemption || {};
        const bal = orderRes.data.balance || {};
        const newTotalPaise = Number(bal.total ?? 0);
        const newRealPaise = Number(bal.real ?? 0);
        const newPromoPaise = Number(bal.promo ?? 0);
        setState((prev) => ({
          loading: false,
          data: {
            ...(prev.data || {}),
            balance_paise: newTotalPaise,
            balance_rupees: newTotalPaise / 100,
            real_paise: newRealPaise,
            real_rupees: newRealPaise / 100,
            promo_paise: newPromoPaise,
            promo_rupees: newPromoPaise / 100,
          },
        }));
        toast.success(
          cr.message
            || `Coupon ${cr.coupon_code} redeemed — ₹${cr.credited_rupees ?? 0} promotional credit added to your wallet.`,
          { duration: 6000 }
        );
        setCouponApplied(null);
        setAmount("");
        try { await load(); } catch { /* eager update already applied */ }
        setBusy(false);
        return;
      }

      const { razorpay, billing } = orderRes.data;

      const finalize = async (resp) => {
        const r = await api.walletTopupConfirm({
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        });
        // ── Eager balance update — the confirm response already
        // carries the authoritative new balance. Applying it directly
        // guarantees Current Balance reflects the top-up the moment
        // Razorpay's handler resolves, without waiting on a separate
        // /wallet GET round-trip. `load()` is still awaited below
        // (transactions/history refresh + reconciliation).
        const newPaise = Number(r.data?.new_balance_paise ?? 0);
        const newRupees = Number(r.data?.new_balance_rupees ?? newPaise / 100);
        // Phase 17 — split fields (real/promo) so the two-bucket UI
        // reflects the top-up immediately. Fallback: if the backend
        // omitted these, keep the previous split and only bump the
        // total (the reconciliation load() below will fix it).
        const realPaise = Number(r.data?.real_paise);
        const promoPaise = Number(r.data?.promo_paise);
        setState((prev) => ({
          loading: false,
          data: {
            ...(prev.data || {}),
            balance_paise: newPaise,
            balance_rupees: newRupees,
            ...(Number.isFinite(realPaise) ? {
              real_paise: realPaise,
              real_rupees: realPaise / 100,
            } : {}),
            ...(Number.isFinite(promoPaise) ? {
              promo_paise: promoPaise,
              promo_rupees: promoPaise / 100,
            } : {}),
          },
        }));
        toast.success(`Wallet topped up! New balance ${rupeesR(newRupees)}`);
        // Clear applied coupon since it was just consumed / not re-usable.
        setCouponApplied(null);
        // Reconcile against the full server view (recent_transactions
        // list, updated vendor GSTIN, etc.). If this fails for any
        // reason, the eager update above still keeps Current Balance
        // correct.
        try { await load(); } catch { /* eager update already applied */ }
      };

      if (razorpay.mock_mode) {
        // Mock mode is now ONLY reachable when the backend has been
        // explicitly configured with RAZORPAY_MOCK_MODE=true (e.g.
        // local dev). On production this branch is unreachable — see
        // backend core/billing.py hardening.
        //
        // Even in explicit mock mode, we NO LONGER auto-confirm
        // silently — that's the third leg of the vulnerability we
        // closed. Instead we surface a very visible "you are in mock
        // mode" prompt so a developer can consciously proceed.
        toast.warning(
          "Payment gateway is in mock mode. This is a development-only path; " +
          + "no real charge will occur. Ask a super admin to confirm this top-up.",
          { duration: 10000 }
        );
        setBusy(false);
        return;
      }

      const ok = await ensureRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Couldn't load Razorpay Checkout. Please retry.");
        setBusy(false);
        return;
      }
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay({
        key: razorpay.key_id,
        amount: razorpay.amount_paise,
        currency: razorpay.currency,
        name: "CityDooh Wallet",
        description: `Top-up: ${rupeesR(billing.base_amount)} (+ GST ${rupeesR(billing.gst_amount)})`,
        order_id: razorpay.order_id,
        theme: { color: "#fdd23a" },
        handler: async (resp) => {
          try {
            await finalize(resp);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Confirmation failed");
          } finally {
            setBusy(false);
            if (safetyTimer) clearTimeout(safetyTimer);
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(false);
            if (safetyTimer) clearTimeout(safetyTimer);
          },
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed");
        setBusy(false);
        if (safetyTimer) clearTimeout(safetyTimer);
      });
      // Failsafe — if Razorpay's own lifecycle callbacks never fire
      // (e.g. modal flashes closed on an invalid order, iframe blocked
      // by an extension, or JS execution stalls), reset the button so
      // the vendor can retry instead of being stuck on "Processing…"
      // forever. 90s comfortably outlasts any legitimate checkout.
      const safetyTimer = setTimeout(() => {
        setBusy(false);
        toast.error("Checkout didn't complete. Please try again.");
      }, 90000);
      try {
        rzp.open();
      } catch (openErr) {
        clearTimeout(safetyTimer);
        setBusy(false);
        toast.error(openErr?.message || "Couldn't open checkout. Please retry.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start top-up");
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }

  const { data } = state;
  const balancePaise = data?.balance_paise ?? 0;
  const balance = data?.balance_rupees ?? 0;
  // Phase 17 split. When the backend hasn't populated the split yet
  // (pre-migration vendor), the legacy balance is treated as
  // ENTIRELY real (top-ups never went to promo before), matching the
  // server-side fallback in core.wallet.get_balances().
  const realPaise = Number.isFinite(Number(data?.real_paise))
    ? Number(data.real_paise)
    : balancePaise;
  const promoPaise = Number.isFinite(Number(data?.promo_paise))
    ? Number(data.promo_paise)
    : 0;
  const txns = data?.recent_transactions || [];

  // Compute a rough "leads / emails you can still afford" hint. Uses a
  // ₹1 overage assumption + ₹0.15 email rate — friendly not exact.
  const leadsAffordable = Math.floor(balancePaise / 100);
  const emailsAffordable = Math.floor(balancePaise / 15);

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div>
        <span className="mkt-tape">Wallet</span>
        <h1 className="ag-page-title mt-2">
          Your <span className="mkt-highlight">prepaid</span> balance
        </h1>
        <p className="text-sm text-black/70 mt-2 max-w-2xl">
          Retargeting sends and lead-overage charges (beyond the 1,000 leads
          included per paid campaign) come from this wallet. Top up any time —
          balance never expires.
        </p>
      </div>

      {/* Balance */}
      <Card className="p-6 md:p-8" data-testid="wallet-balance-card">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-black">
              <WalletIcon className="h-5 w-5" />
              <p className="text-xs font-bold uppercase tracking-widest">Current balance</p>
            </div>
            <p className="font-display text-6xl md:text-7xl leading-none tabular-nums mt-3" data-testid="wallet-balance-amount">
              {rupeesR(balance)}
            </p>
            <p className="text-xs text-black/60 mt-2">
              ≈ {leadsAffordable} extra leads · {emailsAffordable} email reminders
            </p>

            {/* Phase 17 split — only render the two-row breakdown when
                a promo grant exists. Vendors with no promo credit see
                the single big number they've always seen, avoiding
                unnecessary visual clutter. */}
            {promoPaise > 0 && (
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <div
                  className="border-2 border-black bg-white px-3 py-2.5"
                  data-testid="wallet-balance-real"
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-black/60">
                    Purchased balance
                  </p>
                  <p className="font-display text-2xl tabular-nums leading-tight" data-testid="wallet-balance-real-amount">
                    {rupees(realPaise)}
                  </p>
                  <p className="text-[10px] text-black/50 mt-0.5">
                    Funded by your top-ups
                  </p>
                </div>
                <div
                  className="border-2 border-black bg-[var(--graffiti-yellow)] px-3 py-2.5"
                  data-testid="wallet-balance-promo"
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-black/70">
                    Promotional credit
                  </p>
                  <p className="font-display text-2xl tabular-nums leading-tight" data-testid="wallet-balance-promo-amount">
                    {rupees(promoPaise)}
                  </p>
                  <p className="text-[10px] text-black/60 mt-0.5">
                    Spent first · granted by CityDooh
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Top up */}
      <Card className="p-5 md:p-6 space-y-4" data-testid="wallet-topup-card">
        <div className="flex items-center gap-2 border-b-2 border-black pb-3">
          <Plus className="h-5 w-5" />
          <h2 className="font-heading text-base font-bold uppercase tracking-wide">Top up</h2>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {QUICK_PICKS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => {
                setAmount(q);
                if (couponApplied) setCouponApplied(null);
              }}
              className={`ag-btn-ghost !text-sm !py-3 !px-2 justify-center ${
                Number(amount) === q ? "!bg-[var(--graffiti-yellow)] !border-black" : ""
              }`}
              data-testid={`wallet-quick-${q}`}
            >
              ₹{q.toLocaleString("en-IN")}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="uppercase text-[11px] font-bold tracking-wider">Custom amount (min ₹100)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-black font-bold">₹</span>
              <Input
                type="number"
                min={100}
                step={100}
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  // Any amount change invalidates the applied coupon
                  // because the discount was computed off the old
                  // base. Force the vendor to re-apply.
                  if (couponApplied) setCouponApplied(null);
                }}
                className="pl-7 h-11 border-2 border-black bg-white text-lg font-bold"
                data-testid="wallet-custom-amount"
              />
            </div>
            <p className="text-xs text-black/60" data-testid="wallet-topup-hint">
              {couponApplied ? (
                <>
                  You&#39;ll be charged ₹{(Math.max(0, Number(amount) - Number(couponApplied.discount || 0)) * 1.18).toFixed(2)} incl. 18% GST (₹{Number(couponApplied.discount).toFixed(2)} off).
                  <br />
                  <span className="text-[#176b3a] font-semibold">
                    + ₹{Number(couponApplied.discount).toFixed(2)} coupon bonus credited to your promo balance.
                  </span>
                </>
              ) : (
                <>You&#39;ll be charged ₹{(Number(amount) * 1.18).toFixed(2)} incl. 18% GST.</>
              )}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="uppercase text-[11px] font-bold tracking-wider">GSTIN (for invoice)</Label>
            <Input
              value={gstNumber}
              onChange={(e) => setGstNumber(e.target.value)}
              readOnly={!!vendorGst}
              placeholder="Optional — 15-char GSTIN"
              className="h-11 border-2 border-black bg-white"
              data-testid="wallet-gstin-input"
            />
            {vendorGst ? (
              <p className="text-xs text-black/60">
                Using GSTIN on file.{" "}
                <Link to="/vendor/settings" className="underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2 font-semibold text-black">
                  Edit
                </Link>
              </p>
            ) : (
              <p className="text-xs text-black/60">Leave blank if you don&#39;t need GST input.</p>
            )}
          </div>
        </div>

        <div
          className="pt-1 border-t border-black/10"
          data-testid="wallet-coupon-block"
        >
          <CouponApplyField
            testIdPrefix="wallet-coupon"
            applied={couponApplied}
            disabled={busy}
            onRemove={() => setCouponApplied(null)}
            onApply={async (code) => {
              const rup = Number(amount);
              if (!Number.isFinite(rup) || rup < 100) {
                throw new Error("Enter a valid top-up amount first.");
              }
              const r = await api.previewBillingCoupon({
                code,
                base_amount: rup,
                surface: "wallet_topup",
              });
              setCouponApplied(r.data.billing_coupon);
              const disc = r.data.billing_coupon.discount;
              toast.success(`Coupon ${r.data.billing_coupon.code} applied — you save ₹${Number(disc).toFixed(2)}`);
            }}
          />
        </div>

        <button
          type="button"
          onClick={topup}
          disabled={busy}
          className="mkt-btn w-full justify-center py-3.5"
          data-testid="wallet-topup-button"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Processing…
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" /> Top up {couponApplied
                ? rupeesR(Math.max(0, Number(amount) - Number(couponApplied.discount || 0)) * 1.18)
                : rupeesR(Number(amount) * 1.18)}
            </>
          )}
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-semibold text-black/70">
          <TrustNote icon={ShieldCheck}>Razorpay secure</TrustNote>
          <TrustNote icon={TrendingUp}>Balance never expires</TrustNote>
          <TrustNote icon={Receipt}>GST invoice in Billing</TrustNote>
        </div>
      </Card>

      {/* Recent transactions */}
      <Card className="p-5 md:p-6" data-testid="wallet-history-card">
        <div className="flex items-center justify-between border-b-2 border-black pb-3 mb-3">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            <h2 className="font-heading text-base font-bold uppercase tracking-wide">Recent activity</h2>
          </div>
          <Link
            to="/vendor/wallet/ledger"
            className="text-xs font-bold uppercase tracking-wider underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2 hover:decoration-black"
            data-testid="wallet-view-full-ledger-link"
          >
            View full ledger →
          </Link>
        </div>
        {txns.length === 0 ? (
          <div className="ag-empty">No wallet transactions yet.</div>
        ) : (
          <ul className="divide-y divide-black/10" data-testid="wallet-history">
            {txns.map((t) => {
              // A row is a CREDIT (money added to wallet) if either:
              //   - the shadow ``type`` is one of the credit types, OR
              //   - the amount is stored positive on an admin_adjustment
              //     (legacy admin credits use ``type=admin_adjustment``
              //     with positive amount_paise, admin debits use negative).
              // Everything else (``type=debit`` or negative admin_adjust
              // amount) is a debit. This mirrors ``shadow_write_wallet_
              // transaction`` in ``core/ledger.py`` — see the type_val
              // dispatch there.
              const CREDIT_TYPES = ["topup", "promo_grant"];
              const amt = Number(t.amount_paise) || 0;
              const isCredit =
                CREDIT_TYPES.includes(t.type) ||
                (t.type === "admin_adjustment" && amt >= 0);
              const displayAmount = Math.abs(amt);
              return (
                <li key={t.id} className="py-3 flex items-start justify-between gap-3" data-testid={`wallet-row-${t.id}`}>
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`h-9 w-9 rounded-full grid place-items-center border-2 border-black shrink-0 ${
                        isCredit ? "bg-[#d8f1c8] text-[#176b3a]" : "bg-[var(--graffiti-yellow)] text-black"
                      }`}
                    >
                      {isCredit ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-black">
                        {REASON_LABEL[t.reason] || t.reason}
                      </p>
                      <p className="text-[11px] text-black/60">
                        {new Date(t.created_at).toLocaleString()}
                        {t.reference_id ? ` · ref ${String(t.reference_id).slice(0, 8)}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-heading text-base font-bold tabular-nums ${isCredit ? "text-[#176b3a]" : "text-black"}`} data-testid={`wallet-row-amount-${t.id}`}>
                      {isCredit ? "+" : "−"}{rupees(displayAmount)}
                    </p>
                    <p className="text-[11px] text-black/50 tabular-nums">bal {rupees(t.balance_after_paise)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

const TrustNote = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 rounded-lg border-2 border-black bg-white px-3 py-1.5">
    <Icon className="h-3.5 w-3.5 text-black" />
    <span>{children}</span>
  </div>
);
